import { type Time as TimeValue } from '@internationalized/date';

import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';

import type { DateValue } from '@internationalized/date';

import { DiscountType } from '../components/types';

import { discountFormDirtyAtom } from './discountDetailsDrawerAtoms';

// =============================================================================
// Form State
// =============================================================================

export interface DiscountFormState {
	// Section 1: Type & Method (CXP-2313)
	discountType: DiscountType;
	method: 'code' | 'automatic';
	code: string;
	title: string;

	// Section 2: Value (CXP-2314 — future)
	amountType: 'fixed' | 'percentage';
	amount: string;
	appliesTo: 'all' | 'specific';
	applicableProducts: string[];

	// Section 3: Active Dates (CXP-2315)
	startDate: DateValue | null;
	startTime: TimeValue | null;
	hasEndDate: boolean;
	endDate: DateValue | null;
	endTime: TimeValue | null;
}

export const DISCOUNT_FORM_DEFAULTS: DiscountFormState = {
	discountType: DiscountType.AMOUNT_OFF_PRODUCTS,
	method: 'code',
	code: '',
	title: '',

	amountType: 'fixed',
	amount: '',
	appliesTo: 'all',
	applicableProducts: [],

	startDate: null,
	startTime: null,
	hasEndDate: false,
	endDate: null,
	endTime: null,
};

export const discountFormAtom = atom<DiscountFormState>(DISCOUNT_FORM_DEFAULTS);

// =============================================================================
// Form Validation
// =============================================================================

export type DiscountFormErrors = Partial<Record<keyof DiscountFormState, string>>;

export const discountFormErrorsAtom = atom<DiscountFormErrors>({});

/** Validates the form and returns errors. Empty object means valid. */
export const validateDiscountForm = (state: DiscountFormState): DiscountFormErrors => {
	const errors: DiscountFormErrors = {};

	// Method-dependent field: code for 'code' method, title for 'automatic'
	if (state.method === 'code' && !state.code.trim()) {
		errors.code = 'Discount code is required';
	} else if (state.method === 'code' && /\s/.test(state.code)) {
		errors.code = 'Discount code cannot contain spaces';
	}
	if (state.method === 'automatic' && !state.title.trim()) {
		errors.title = 'Title is required';
	}

	// Amount is always required and must be greater than 0
	const parsedAmount = Number(state.amount);
	if (!state.amount.trim() || parsedAmount <= 0) {
		errors.amount = 'Amount must be greater than 0';
	} else if (state.amountType === 'percentage' && parsedAmount > 100) {
		errors.amount = 'Percentage must be between 0 and 100';
	}

	// Start date is always required
	if (!state.startDate) {
		errors.startDate = 'Start date is required';
	}

	// End date is required when hasEndDate is checked
	if (state.hasEndDate && !state.endDate) {
		errors.endDate = 'End date is required';
	}

	return errors;
};

/** Hook that returns the error string (or undefined) for a given field. */
export const useDiscountFieldError = (field: keyof DiscountFormState): string | undefined => {
	const errors = useAtomValue(discountFormErrorsAtom);
	return errors[field];
};

// =============================================================================
// Helper Hook
// =============================================================================

export const useDiscountFormField = <K extends keyof DiscountFormState>(
	field: K,
): [DiscountFormState[K], (value: DiscountFormState[K]) => void] => {
	const [formState, setFormState] = useAtom(discountFormAtom);
	const setDirty = useSetAtom(discountFormDirtyAtom);
	const setErrors = useSetAtom(discountFormErrorsAtom);

	const setValue = useCallback(
		(value: DiscountFormState[K]) => {
			setFormState((prev) => ({ ...prev, [field]: value }));
			setDirty(true);
			// Clear the error for this field when the user edits it
			setErrors((prev) => {
				if (!prev[field]) return prev;
				const next = { ...prev };
				delete next[field];
				return next;
			});
		},
		[field, setFormState, setDirty, setErrors],
	);

	return [formState[field], setValue];
};
